<!-- SelfAssessment.vue -->
<template>
  <main class="tabs-page" aria-labelledby="tabs-title">
    <header class="page-head">
      <h1 id="tabs-title" class="title">ESG Governance 진단</h1>
    </header>

    <section class="tabs-grid" aria-label="자가진단 설문 탭 영역">
      <nav
        class="tablist"
        role="tablist"
        aria-label="자가진단 설문 목록"
        @keydown="onKeydown"
        ref="tablistRef"
      >
        <button
          v-for="(t, i) in tabs"
          :key="t.id"
          class="tab"
          :class="{ 'is-disabled': !canAccessVisual(t), 'is-paid': isPaidTab(t) }"
          role="tab"
          :id="t.id"
          :tabindex="activeIndex === i ? 0 : -1"
          :aria-selected="activeIndex === i ? 'true' : 'false'"
          :aria-controls="t.panelId"
          :aria-disabled="!canAccessVisual(t) ? 'true' : 'false'"
          @click="onClickTab(t)"
          @keydown.enter.prevent="onClickTab(t)"
          @keydown.space.prevent="onClickTab(t)"
        >
          <span class="label">
            {{ t.label }}
          </span>
          <span v-if="isPaidTab(t)" class="sr-only">(유료회원 전용 탭)</span>
        </button>
      </nav>

      <section class="panels">
        <h2 class="sr-only">설문 패널</h2>
        <KeepAlive>
          <component :is="currentComponent" />
        </KeepAlive>
      </section>
    </section>
  </main>
</template>

<script setup>
import { ref, computed, onMounted, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import SurveyPublic from '@/pages/iicc/selfassessment/survey/SurveyPublic.vue'
import SurveyAuditCommittee from '@/pages/iicc/selfassessment/survey/SurveyAuditCommittee.vue'
import SurveyBoard from '@/pages/iicc/selfassessment/survey/SurveyBoard.vue'
import SurveyInternalAuditor from '@/pages/iicc/selfassessment/survey/SurveyInternalAuditor.vue'
import SurveyExecutive from '@/pages/iicc/selfassessment/survey/SurveyExecutive.vue'
import ExternalAuditor from '@/pages/iicc/selfassessment/survey/ExternalAuditor.vue'

const router = useRouter()
const route = useRoute()
const store = useStore()

const tabs = [
  { key: 'surveypublic', name: 'surveypublic', id: 'tab-surveypublic', panelId: 'panel-surveypublic', label: '거버넌스 진단(Sample)' },
  { key: 'surveyinternalauditor', name: 'surveyinternalauditor', id: 'tab-surveyinternalauditor', panelId: 'panel-surveyinternalauditor', label: '내부감사인' },
  { key: 'externalauditor', name: 'externalauditor', id: 'tab-externalauditor', panelId: 'panel-externalauditor', label: '외부감사인' },
  { key: 'surveyauditcommittee', name: 'surveyauditcommittee', id: 'tab-surveyauditcommittee', panelId: 'panel-surveyauditcommittee', label: '감사위원회' },
  { key: 'surveyboard', name: 'surveyboard', id: 'tab-surveyboard', panelId: 'panel-surveyboard', label: '이사회' },
  { key: 'surveyexecutive', name: 'surveyexecutive', id: 'tab-surveyexecutive', panelId: 'panel-surveyexecutive', label: '최고경영진' },
]

const tablistRef = ref(null)

const checkUserId = computed(() => store.state.temp?.userInfo)
const isLoggedIn = computed(() => !!checkUserId.value && !!(checkUserId.value.userId || checkUserId.value.authToken))
const isFreePlan = computed(() => checkUserId.value?.planId === 'P00001')
const isPaidMember = computed(() => isLoggedIn.value && !isFreePlan.value)

const paidKeys = new Set([
  'surveyauditcommittee',
  'surveyboard',
  'surveyinternalauditor',
  'surveyexecutive',
  'externalauditor',
])

const isPaidTab = (t) => paidKeys.has(t.key)
const isFreeTab = (t) => t.key === 'surveypublic'
const canAccess = (t) => isFreeTab(t) || (isPaidTab(t) && isPaidMember.value)
const canAccessVisual = (t) => canAccess(t)

const allowedSubTabs = tabs.map(t => t.name)

const activeSubTab = computed(() => {
  const subTab = route.query.subTab
  return allowedSubTabs.includes(subTab) ? subTab : 'surveypublic'
})

const activeIndex = computed(() => {
  const idx = tabs.findIndex(t => t.name === activeSubTab.value)
  return idx >= 0 ? idx : 0
})

const currentComponent = computed(() => {
  switch (activeSubTab.value) {
    case 'surveyinternalauditor':
      return SurveyInternalAuditor
    case 'externalauditor':
      return ExternalAuditor
    case 'surveyauditcommittee':
      return SurveyAuditCommittee
    case 'surveyboard':
      return SurveyBoard
    case 'surveyexecutive':
      return SurveyExecutive
    case 'surveypublic':
    default:
      return SurveyPublic
  }
})

function onClickTab(t) {
  if (isPaidTab(t) && !isPaidMember.value) {
    alert('해당 자료는 심화 분석 기능과 함께 제공됩니다.\n조직 맞춤형 상세 자료가 필요하시면 안내해드리겠습니다.\n문의: 070-4489-7447')
    return
  }

  router.push({
    path: '/iiccmain',
    query: {
      ...route.query,
      tab: 'selfassessment',
      subTab: t.name,
    },
  })
}

function guardRoute(subTabName) {
  const curTab = tabs.find(t => t.name === subTabName)
  if (!curTab) return

  if (isPaidTab(curTab) && !isPaidMember.value) {
    alert('해당 자료는 심화 분석 기능과 함께 제공됩니다.\n조직 맞춤형 상세 자료가 필요하시면 안내해드리겠습니다.\n문의: 070-4489-7447')
    router.replace({
      path: '/iiccmain',
      query: {
        ...route.query,
        tab: 'selfassessment',
        subTab: 'surveypublic',
      },
    })
  }
}

function onKeydown(e) {
  const max = tabs.length - 1
  const i = activeIndex.value

  if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) {
    e.preventDefault()
  }

  const move = (nextIndex) => {
    const btns = tablistRef.value?.querySelectorAll('.tab')
    btns?.[nextIndex]?.focus()
  }

  if (e.key === 'ArrowRight') move(Math.min(i + 1, max))
  if (e.key === 'ArrowLeft') move(Math.max(i - 1, 0))
  if (e.key === 'Home') move(0)
  if (e.key === 'End') move(max)
}

watch(
  () => route.query.subTab,
  (newSubTab) => {
    const i = activeIndex.value
    const btns = tablistRef.value?.querySelectorAll('.tab')
    btns?.[i]?.focus()
    guardRoute(newSubTab)
  }
)

watchEffect(() => {
  if (route.query.tab !== 'selfassessment') return

  const subTab = route.query.subTab
  if (!allowedSubTabs.includes(subTab)) {
    router.replace({
      path: '/iiccmain',
      query: {
        ...route.query,
        tab: 'selfassessment',
        subTab: 'surveypublic',
      },
    })
  }
})

onMounted(() => {
  guardRoute(activeSubTab.value)
})
</script>

<style scoped>
.tabs-page {
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Noto Sans KR', sans-serif;
}

.title {
  font-size: 1.625rem;
  font-weight: 700;
}

.desc,
.panels p {
  font-size: 0.95rem;
}

.tabs-page {
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 20px;
  padding: 20px 16px 40px;
  background: #fff;
}

.page-head {
  display: grid;
  gap: 8px;
}

.tabs-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.tablist {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
}

.tab {
  display: grid;
  grid-auto-flow: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 12px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #f9fafb;
  font-weight: 600;
  cursor: pointer;
}

.tab[aria-selected='true'] {
  background: #1d2088;
  color: #fff;
}

.tab:focus-visible {
  outline: 2px solid #111827;
  outline-offset: 2px;
}

.panels {
  display: grid;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.badge {
  margin-left: .5rem;
  font-size: .75rem;
  padding: .1rem .4rem;
  border-radius: .5rem;
  border: 1px solid currentColor;
}

@media (max-width: 640px) {
  .tab {
    padding: 10px 8px;
    font-size: 0.95rem;
  }
}
</style>